/* import __COLOCATED_TEMPLATE__ from './ticket-types.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type TicketTypeSummary from 'embercom/models/inbox/ticket-type-summary';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type RouterService from '@ember/routing/router-service';
import { taskFor } from 'ember-concurrency-ts';
import { dropTask } from 'ember-concurrency-decorators';
import TicketType from 'embercom/models/inbox/ticket-type';
import { TicketCategory } from 'embercom/objects/inbox/ticket';
import type TicketStateService from 'embercom/services/ticket-state-service';

interface Args {
  ticketTypes: TicketTypeSummary[];
  createNewTicketType: () => void;
}

type TabView = 'live' | 'archived';

export interface ColumnConfig {
  label: string;
  valuePath: 'name' | 'description' | 'createdAt' | 'edit' | 'internal';
  isVisible: boolean;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
}

const LOCALIZATION_KEY = 'settings.ticket-data.list-ticket-types';

export default class TicketTypes extends Component<Args> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare notificationsService: any;
  @service declare ticketStateService: TicketStateService;

  @tracked showTicketTypeSideDrawer = false;
  @tracked currentTab: TabView = 'live';
  @tracked createTicketTypeCategory: TicketCategory | undefined;

  get currentTabLabel() {
    return this.currentTab === 'live'
      ? this.intl.t('settings.ticket-data.list-ticket-live-tab')
      : this.intl.t('settings.ticket-data.list-ticket-archived-tab');
  }

  get ticketTypes() {
    return this.args.ticketTypes.sortBy('name');
  }

  get liveTicketTypes() {
    return this.ticketTypes.rejectBy('archived');
  }

  get archivedTicketTypes() {
    return this.ticketTypes.filterBy('archived');
  }

  get liveTicketTypesByCategory() {
    return TicketType.ticketTypesByCategory(this.liveTicketTypes) as Record<
      TicketCategory,
      TicketType[]
    >;
  }

  get archivedTicketTypesByCategory() {
    return TicketType.ticketTypesByCategory(this.archivedTicketTypes) as Record<
      TicketCategory,
      TicketType[]
    >;
  }

  get commonColumnsSettings(): ColumnConfig[] {
    return [
      {
        label: this.intl.t(`${LOCALIZATION_KEY}.table-columns.name`),
        valuePath: 'name',
        isVisible: true,
        width: '20%',
        minWidth: '200px',
      },
      {
        label: this.intl.t(`${LOCALIZATION_KEY}.table-columns.description`),
        valuePath: 'description',
        isVisible: true,
        width: '50%',
      },
      {
        label: this.intl.t(`${LOCALIZATION_KEY}.table-columns.created-at`),
        valuePath: 'createdAt',
        isVisible: true,
        width: '130px',
      },
      {
        label: '',
        valuePath: 'edit',
        isVisible: true,
        width: '160px',
      },
    ];
  }

  get taskColumnsSettings() {
    let createdAtColumnIndex = this.commonColumnsSettings.findIndex(
      (column) => column.valuePath === 'createdAt',
    );

    return this.commonColumnsSettings.insertAt(createdAtColumnIndex, {
      label: this.intl.t(`${LOCALIZATION_KEY}.table-columns.visibility`),
      valuePath: 'internal',
      isVisible: true,
      width: '146px',
    });
  }

  get categorySections() {
    return [
      {
        id: TicketCategory.Request,
        title: this.intl.t(`${LOCALIZATION_KEY}.ticket-type.request`),
        intro: this.intl.t(`${LOCALIZATION_KEY}.ticket-type.ticket-intervention-request-info`),
        icon: 'ticket',
        columns: this.commonColumnsSettings,
      },
      {
        id: TicketCategory.Tracker,
        title: this.intl.t(`${LOCALIZATION_KEY}.ticket-type.tracker`),
        intro: this.intl.t(`${LOCALIZATION_KEY}.ticket-type.ticket-intervention-tracker-info`),
        icon: 'tracker',
        columns: this.commonColumnsSettings,
      },
      {
        id: TicketCategory.Task,
        title: this.intl.t(`${LOCALIZATION_KEY}.ticket-type.task`),
        intro: this.intl.t(`${LOCALIZATION_KEY}.ticket-type.ticket-intervention-task-info`),
        icon: 'back-office',
        columns: this.taskColumnsSettings,
      },
    ];
  }

  @action
  onCreateTicketType(ticketCategory: TicketCategory) {
    this.createTicketTypeCategory = ticketCategory;
    this.showTicketTypeSideDrawer = true;
  }

  @action private sortData() {
    this.ticketTypes.sortBy('name');
  }

  @action switchTab(tab: TabView) {
    this.currentTab = tab;
  }

  @action onEditTicketType(ticketTypeId: number) {
    window?.scrollTo(0, 0);
    this.router.transitionTo(
      'apps.app.settings.helpdesk.tickets.ticket-types.detail',
      ticketTypeId,
    );
  }

  @action onRestoreTicketType(ticketType: TicketType) {
    taskFor(this.unarchiveTicketType).perform(ticketType);
  }

  @action onTicketTypeSideDrawerClose() {
    this.showTicketTypeSideDrawer = false;
    this.createTicketTypeCategory = undefined;
  }

  @action onTicketTypeCreated(ticketType: TicketTypeSummary) {
    this.ticketTypes.push(ticketType);
  }

  @dropTask
  *unarchiveTicketType(ticketType: TicketType) {
    try {
      ticketType.setProperties({
        archived: false,
      });

      yield ticketType.save();
      this.ticketStateService.updateTicketType(ticketType.id, { archived: false });
      this.notificationsService.notifyConfirmation(
        this.intl.t('settings.ticket-data.list-ticket-unarchive-success', {
          ticketTypeName: ticketType.name,
        }),
      );

      this.sortData();

      if (this.archivedTicketTypes.length === 0) {
        this.switchTab('live');
      }
    } catch (error) {
      ticketType.rollbackAttributes();
      if (error.jqXHR?.responseJSON?.message) {
        this.notificationsService.notifyError(error.jqXHR.responseJSON.message);
      } else {
        this.notificationsService.notifyError(
          this.intl.t('settings.ticket-data.list-ticket-unarchive-failure', {
            ticketTypeName: ticketType.name,
          }),
        );
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Helpdesk::Tickets::TicketTypes': typeof TicketTypes;
  }
}
